"use client";

import { motion } from "framer-motion";
import {
  Building2,
  Plane,
  Calculator,
  TrendingUp,
  CreditCard,
  ShieldCheck,
  Users,
  Zap,
  ArrowRight,
} from "lucide-react";
import Link from "next/link";

const services = [
  {
    icon: <CreditCard size={26} />,
    title: "Bank Account Opening",
    description:
      "Open your business bank account with confidence through expert guidance, document preparation, and banking partner coordination.",
    href: "/essential-services/bank-account-opening-in-uae",
  },
  {
    icon: <Calculator size={26} />,
    title: "Accounting Services",
    description:
      "Professional bookkeeping, financial reporting, payroll management, VAT filing, and audit support for complete financial control.",
    href: "/essential-services/accounting-services",
  },
  {
    icon: <ShieldCheck size={26} />,
    title: "Compliance Services",
    description:
      "Stay compliant with UAE regulations through structured compliance management, filings, and regulatory support.",
    href: "/essential-services/compliance-services",
  },
  {
    icon: <Users size={26} />,
    title: "Health Insurance",
    description:
      "Comprehensive individual and corporate health insurance solutions designed to protect employees and business owners.",
    href: "/essential-services/health-insurance",
  },
  {
    icon: <TrendingUp size={26} />,
    title: "VAT & Tax Consultancy",
    description:
      "Expert guidance for VAT registration, tax planning, filings, and corporate tax compliance requirements.",
    href: "/essential-services/corporate-tax",
  },
  {
    icon: <Building2 size={26} />,
    title: "IT Services",
    description:
      "Reliable business technology solutions including hosting, domains, email setup, maintenance, and technical support.",
    href: "/essential-services/it-services",
  },
  {
    icon: <Zap size={26} />,
    title: "Digital Marketing",
    description:
      "Website development, SEO, branding, content creation, and digital campaigns that drive measurable growth.",
    href: "/essential-services/digital-marketing",
  },
  {
    icon: <Plane size={26} />,
    title: "Workforce Solutions",
    description:
      "Delivering reliable manpower solutions designed to support your operations with speed, precision, and confidence.",
    href: "/workforce-solutions",
  },
];

export default function ServicesOverview() {
  return (
    <section className="relative py-20 lg:py-24 bg-white overflow-hidden">
      <div className="main-container">

        {/* HEADER */}
        <div className="max-w-4xl mx-auto text-center mb-16 lg:mb-20">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#B8893A] font-semibold mb-4">
            Professional Services
          </span>

          <h2
            className="font-bold text-[#0A0A0A]"
            style={{
              fontSize: "clamp(32px, 5vw, 54px)",
              lineHeight: "1.2",
            }}
          >
            Essential Services For
            <span className="font-serif font-normal text-[#B8893A]"> Modern Businesses</span>
          </h2>

          <p
            className="text-gray-500 mt-6 mx-auto max-w-3xl"
            style={{
              fontSize: "clamp(14px,1.2vw,16px)",
              lineHeight: "1.7",
            }}
          >
            From banking and accounting to compliance, insurance,
            taxation, and digital solutions, we provide the professional
            services businesses need to operate efficiently and grow with
            confidence.
          </p>
        </div>

        {/* SERVICES GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 px-4 sm:px-6 lg:px-8">

          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="group bg-[#FAFAFA] border border-[#ECECEC] rounded-3xl p-7 hover:bg-white hover:border-[#B8893A]/20 hover:shadow-xl transition-all duration-300 flex flex-col"
            >
              {/* ICON */}
              <div className="w-14 h-14 rounded-2xl bg-[#B8893A]/10 text-[#B8893A] flex items-center justify-center mb-6">
                {service.icon}
              </div>

              {/* TITLE */}
              <h3
                className="font-semibold text-[#0A0A0A] mb-3"
                style={{
                  fontSize: "clamp(18px,2vw,22px)",
                  lineHeight: "1.3",
                }}
              >
                {service.title}
              </h3>

              {/* DESCRIPTION */}
              <p
                className="text-gray-600 flex-grow"
                style={{
                  fontSize: "clamp(14px,1.1vw,15px)",
                  lineHeight: "1.7",
                }}
              >
                {service.description}
              </p>

              {/* LINK */}
              <Link
                href={service.href}
                className="inline-flex items-center gap-2 text-[#B8893A] font-semibold text-sm mt-6 group-hover:gap-3 transition-all"
              >
                Learn more 
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-20">
          <div className="bg-[#FAFAFA] border border-[#ECECEC] rounded-[32px] p-8 md:p-12 text-center">

            <h3
              className="font-bold text-[#0A0A0A]"
              style={{
                fontSize: "clamp(26px,4vw,40px)",
                lineHeight: "1.2",
              }}
            >
              Need Help Choosing The Right Service?
            </h3>

            <p
              className="text-gray-600 mt-4 max-w-2xl mx-auto"
              style={{
                fontSize: "clamp(14px,1.2vw,16px)",
                lineHeight: "1.7",
              }}
            >
              Our specialists can help you identify the most suitable
              solutions based on your business requirements and operational
              goals.
            </p>

            <div className="mt-8">
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#B8893A] text-white font-semibold hover:bg-[#A67B34] transition-all"
              >
                Speak With A Service Advisor
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>

      </div>

      {/* Decorative Glow */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-[#B8893A]/5 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#B8893A]/5 rounded-full blur-3xl -z-10" />
    </section>
  );
}